import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    getActivationReport, getActivationMembers,
    getActiveTransactors, getTransactorMembers,
    getCommissionReport, getCommissionLedger,
} from "../../services/api";

const errMsg = (e) => e?.message || "Something went wrong. Please try again.";

const make = (name, call) =>
    createAsyncThunk(`performanceReport/${name}`, async (arg, { rejectWithValue }) => {
        try {
            const res = await call(arg);
            // API body is { status, message, data } -> keep only `data` (summary/cohorts, or the paginator)
            if (res?.status?.success === false) throw res;
            return res?.data;
        } catch (e) {
            return rejectWithValue(errMsg(e));
        }
    });

// summary thunks take `filters`; list thunks take { perPage, page, filters }
export const fetchActivationReport = make("activation", (f) => getActivationReport(f));
export const fetchActivationMembers = make("activationMembers", (a) => getActivationMembers(a.perPage, a.page, a.filters));
export const fetchActiveTransactors = make("transactors", (f) => getActiveTransactors(f));
export const fetchTransactorMembers = make("transactorMembers", (a) => getTransactorMembers(a.perPage, a.page, a.filters));
export const fetchCommissionReport = make("commission", (f) => getCommissionReport(f));
export const fetchCommissionLedger = make("commissionLedger", (a) => getCommissionLedger(a.perPage, a.page, a.filters));

const thunks = {
    activation: fetchActivationReport,
    activationMembers: fetchActivationMembers,
    transactors: fetchActiveTransactors,
    transactorMembers: fetchTransactorMembers,
    commission: fetchCommissionReport,
    commissionLedger: fetchCommissionLedger,
};

const blank = () => ({ data: null, loading: false, error: null, reqId: null });
const initialState = Object.fromEntries(Object.keys(thunks).map((k) => [k, blank()]));

const performanceReportSlice = createSlice({
    name: "performanceReport",
    initialState,
    reducers: {},
    extraReducers: (b) => {
        // Each request remembers its id; a late response from an older filter set is ignored.
        Object.entries(thunks).forEach(([key, thunk]) => {
            b.addCase(thunk.pending, (s, a) => {
                s[key].loading = true; s[key].error = null; s[key].reqId = a.meta.requestId;
            })
                .addCase(thunk.fulfilled, (s, a) => {
                    if (s[key].reqId !== a.meta.requestId) return;
                    s[key].loading = false; s[key].data = a.payload;
                })
                .addCase(thunk.rejected, (s, a) => {
                    if (s[key].reqId !== a.meta.requestId) return;
                    s[key].loading = false; s[key].error = a.payload || errMsg(a.error);
                });
        });
    },
});

export default performanceReportSlice.reducer;